import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, defer, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { TICKET_POLICY_SEED } from '@common/data';
import type { AutoTicketRule, TicketPolicy, TicketSlaTarget } from '@common/types';
import { OrganizationService } from './organization.service';

const SAVE_LATENCY_MS = 300;

@Injectable({ providedIn: 'root' })
export class TicketPolicyService {
  private readonly organizations = inject(OrganizationService);
  private readonly state = signal<Record<string, TicketPolicy>>({
    [TICKET_POLICY_SEED.organizationId]: TICKET_POLICY_SEED,
  });

  readonly policy = computed(() => this.forOrganization(this.organizations.activeId() ?? ''));

  forOrganization(id: string): TicketPolicy {
    return this.state()[id] ?? { ...TICKET_POLICY_SEED, organizationId: id, autoTicketRules: [] };
  }

  save(next: TicketPolicy): Observable<TicketPolicy> {
    return defer(() => {
      if (
        ['normal', 'escalated', 'critical'].some(
          (urgency) => next.slaTargets.filter((t) => t.urgency === urgency).length !== 1,
        ) ||
        next.slaTargets.some(
          (t) =>
            !Number.isFinite(t.firstResponseHours) ||
            !Number.isFinite(t.resolutionHours) ||
            t.firstResponseHours <= 0 ||
            t.resolutionHours < t.firstResponseHours,
        ) ||
        !Number.isFinite(next.escalation.afterBreachHours) ||
        next.escalation.afterBreachHours < 0
      ) {
        throw new Error('SLA targets must be positive, with resolution after first response.');
      }
      if (
        next.autoTicketRules.some(
          (rule) =>
            !rule.id ||
            !rule.questionnaireId.trim() ||
            !rule.questionLabel.trim() ||
            (rule.triggerType === 'rating_threshold' &&
              (!rule.questionId.trim() || !Number.isFinite(rule.threshold))),
        )
      ) {
        throw new Error(
          'Rules require stable IDs and a valid question and threshold for rating triggers.',
        );
      }
      this.state.update((all) => ({ ...all, [next.organizationId]: next }));
      return of(next);
    }).pipe(delay(SAVE_LATENCY_MS));
  }

  saveSla(targets: ReadonlyArray<TicketSlaTarget>): Observable<TicketPolicy> {
    return this.save({ ...this.policy(), slaTargets: targets });
  }

  toggleAutoRule(
    id: string,
    organizationId = this.policy().organizationId,
  ): Observable<TicketPolicy> {
    const current = this.forOrganization(organizationId);
    const next: TicketPolicy = {
      ...current,
      autoTicketRules: current.autoTicketRules.map((rule) =>
        rule.id === id ? { ...rule, isEnabled: !rule.isEnabled } : rule,
      ),
    };
    return this.save(next);
  }

  upsertAutoRule(
    rule: AutoTicketRule,
    organizationId = this.policy().organizationId,
  ): Observable<TicketPolicy> {
    const current = this.forOrganization(organizationId);
    const exists = current.autoTicketRules.some((entry) => entry.id === rule.id);
    const rules = exists
      ? current.autoTicketRules.map((entry) => (entry.id === rule.id ? rule : entry))
      : [...current.autoTicketRules, rule];
    return this.save({ ...current, autoTicketRules: rules });
  }

  removeAutoRule(
    id: string,
    organizationId = this.policy().organizationId,
  ): Observable<TicketPolicy> {
    const current = this.forOrganization(organizationId);
    return this.save({
      ...current,
      autoTicketRules: current.autoTicketRules.filter((rule) => rule.id !== id),
    });
  }
}
